import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { mediaSrc } from "../lib/media";
import { t } from "../lib/i18n";
import Seo from "../components/Seo";
import ShareButton from "../components/ShareButton";
import DemoBadge from "../components/DemoBadge";
import { MediaTile, EventCard, ChallengeCard, EmptyState, Loader } from "../components/Blocks";

export default function CreatorDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/creators/${slug}`).then(({ data }) => setP(data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <EmptyState message="Profil introuvable." testid="creator-not-found" />;
  if (!p) return <Loader />;

  return (
    <div data-testid="creator-detail-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
      <Seo
        title={p.display_name}
        description={(p.bio || "").slice(0, 180)}
        path={`/createurs/${p.slug}`}
        image={p.profile_photo_url || (p.media || [])[0]?.url}
        kind="creator"
        type="profile"
      />
      <div className="grid lg:grid-cols-3 gap-12 lg:gap-20">
        <div>
          <div className="aspect-[3/4] overflow-hidden bg-[#0d0d0d]">
            {p.profile_photo_url && (
              <img src={mediaSrc(p.profile_photo_url)} alt={p.display_name} className="w-full h-full object-cover" />
            )}
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <span className="vb-label">{p.city}</span>
            <DemoBadge show={p.is_demo} />
          </div>
          <h1 className="vb-display text-4xl lg:text-6xl font-black" data-testid="creator-name">
            {p.display_name}
          </h1>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="creator-types">
            {(p.profile_types || []).map((tp) => (
              <span key={tp} className="vb-chip !cursor-default">
                {t(`profile_types.${tp}`, tp)}
              </span>
            ))}
          </div>
          <p className="mt-8 text-base text-neutral-300 leading-relaxed max-w-2xl">{p.bio}</p>
          <div className="mt-6">
            <ShareButton
              path={`/createurs/${p.slug}`}
              title={`${p.display_name} — VytaliBoots`}
              text="Découvrez ce profil sur VytaliBoots."
              testid="creator-share-button"
            />
          </div>
          {(p.specialties || []).length > 0 && (
            <div className="mt-8">
              <div className="vb-label mb-3">Spécialités</div>
              <div className="text-sm text-neutral-400">{p.specialties.join(" · ")}</div>
            </div>
          )}
          {p.collaboration_availability && (
            <div className="mt-6 text-sm text-neutral-400">
              <span className="vb-label mr-2">Collaboration</span> {p.collaboration_availability}
            </div>
          )}
        </div>
      </div>

      <div className="mt-20 space-y-16">
        <div>
          <div className="vb-label mb-6">Contenus approuvés</div>
          {(p.media || []).length === 0 ? (
            <EmptyState message="Aucun média publié." testid="creator-media-empty" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4" data-testid="creator-media-grid">
              {p.media.map((m, i) => (
                <MediaTile key={m.id} item={m} testid={`creator-media-${i}`} className="aspect-square" />
              ))}
            </div>
          )}
        </div>
        {(p.events || []).length > 0 && (
          <div>
            <div className="vb-label mb-6">Événements associés</div>
            <div className="grid md:grid-cols-3 gap-6" data-testid="creator-events-grid">
              {p.events.map((e, i) => (
                <EventCard key={e.id} event={e} testid={`creator-event-${i}`} />
              ))}
            </div>
          </div>
        )}
        {(p.challenges || []).length > 0 && (
          <div>
            <div className="vb-label mb-6">Challenges</div>
            <div className="grid md:grid-cols-2 gap-6" data-testid="creator-challenges-grid">
              {p.challenges.map((c, i) => (
                <ChallengeCard key={c.id} challenge={c} testid={`creator-challenge-${i}`} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
